import { useSiteContent } from '@/hooks/useSiteContent';
import ContentState from '@/components/feature/ContentState';

interface InboundData {
  title: string;
  highlight: string;
  titleEnd: string;
  quote: string;
  quoteAuthor: string;
  features: { number: string; title: string; description: string }[];
}

export default function InboundSection() {
  const { data, loading, error, refresh } = useSiteContent<InboundData>('why_us_inbound');

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-primary-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-8">
            {data.title}{' '}
            <span className="text-primary-700">{data.highlight}</span>{' '}
            {data.titleEnd}
          </h2>

          {/* Quote Box */}
          <div className="bg-primary-500 rounded-2xl p-6 md:p-8 text-center mb-12 md:mb-16 max-w-3xl mx-auto">
            <p className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white leading-snug">
              &ldquo;{data.quote}&rdquo;
            </p>
            <p className="text-sm text-white/80 mt-4 font-medium">
              {data.quoteAuthor}
            </p>
          </div>

          {/* Three Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {data.features.map((feature) => (
              <div key={feature.number} className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {feature.number}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}